<template>
  <div
    class="flex flex-col items-center justify-start gap-1 @md:landscape:gap-12 px-[5cqw] @md:landscape:p-12 rounded-2xl"
  >
    <div class="flex justify-between items-center self-stretch gap-3">
      <div class="flex items-center">
        <Icon v-if="isPaidQuiz" icon="mdi:lock" class="size-6 text-fill-gold" />
        <div
          class="flex items-center self-stretch gap-[3cqw] @md:landscape:gap-3"
        >
          <span
            class="text-basics-secondary-80 font-sans text-[13px] font-bold leading-[22px] simulfest:text-[5.5cqw] @md:landscape:text-lg @md:landscape:leading-5"
          >
            {{
              totalQuestions === 1
                ? $t("channel.contests.quiz.questionSingle")
                : $t("channel.contests.quiz.question", {
                    current: currentQuestionNum,
                    total: totalQuestions,
                  })
            }}
          </span>
        </div>
      </div>

      <PointScoreBadge
        v-if="!channelStore.isLeaderboardDisabled"
        coins="200"
        class="size-8 simulfest:size-[7cqw] @md:landscape:w-[3cqh] @md:landscape:h-[3cqh]"
      />
    </div>

    <h1
      class="font-serif text-text text-lg leading-snug @md:landscape:text-3xl w-full @md:landscape:max-w-[33.33rem] font-normal line-clamp-2 break-good"
    >
      {{ questionTitle }}
    </h1>
    <div
      class="flex flex-col gap-1.5 @md:landscape:gap-2 w-full @md:landscape:max-w-[33.33rem] mt-1 mb-1"
    >
      <slot name="main"></slot>
    </div>
  </div>
</template>

<script setup lang="ts">
import PointScoreBadge from "@/components/channel/contests/point-score-badge.vue";
import type { ElementOfArray } from "@/utils/types";
import { type GetQuizzesForChannelQuery } from "@/generated/graphql";
import { computed } from "vue";
import { useObjectTranslation } from "@/stores/translation.store";

import { Icon } from "@iconify/vue";
import { ChannelPermission } from "@f3/shared/permissions";
import { useIsGated } from "@/features/gating/evaluation/use-is-gated";
import { useChannelStore } from "@/stores/channel.store";

const props = defineProps<{
  quiz: ElementOfArray<GetQuizzesForChannelQuery["quizzesForChannel"]>;
  question: ElementOfArray<
    ElementOfArray<
      GetQuizzesForChannelQuery["quizzesForChannel"]
    >["quiz_questions"]
  >;
  totalQuestions: number;
  doneQuestions: number;
  showCorrectAnswer?: boolean;
}>();

const _emit = defineEmits<{
  delete: [];
}>();

const channelStore = useChannelStore();

const currentQuestionNum = computed(() => {
  return Math.min(props.doneQuestions + 1, props.totalQuestions);
});

const isPaidQuiz = useIsGated(
  () => props.quiz.authorization_rule_group,
  ChannelPermission.interactInContests,
);

const { text: questionTitle } = useObjectTranslation(
  computed(() => {
    return {
      authorUserId: props.quiz.created_by?.id,
      originalText: props.question.title,
      objectAccessors: { objectId: props.question.id, objectProperty: "title" },
      translatedText: props.question.quiz_question_translations[0]?.title,
    };
  }),
);
</script>
